import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { CardContainer, LoadingSpinner, StatusChip } from "@msys/ui";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { PowerSettingsNew as PowerIcon } from "@mui/icons-material";
import { useTranslate } from "@tolgee/react";
import { useSnackbar } from "notistack";
import React from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  IntegrationId,
  namedOperations,
} from "../../../../clients/graphqlTypes.js";
import { BreadcrumbItem, Page } from "../../../commons/layout/Page.js";
import { PageContainer } from "../../../commons/layout/PageContainer.js";
import type { IntegrationSettingsProps } from "../integrationRegistry.js";
import { getIntegrationMeta } from "../integrationRegistry.js";
import { integrationErrorMessage } from "../integrationErrorMessage.js";
import { useGenericDashboardQuery } from "./GenericDashboard.generated.js";
import { useIntegrationsOverview_RemoveMutation } from "../IntegrationsOverview.generated.js";
import { useIntegrationSettings_AddMutation } from "./integration-settings/IntegrationSettings.generated.js";
import {
  INTEGRATIONS_BASE_PATH,
  integrationDashboardPath,
  integrationSettingsPath,
} from "../paths.js";
import { NoSettingsCard } from "./NoSettingsCard.js";
import {
  ActivityFeed,
  type ActivityFeedEntry,
} from "./pds-sync/ActivityFeed.js";

export const GenericSettings = ({
  integrationId,
  mode,
}: IntegrationSettingsProps) => {
  const { t } = useTranslate([
    "Integrations",
    "OrganisationPageTopbar",
    "Global",
  ]);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const client = useApolloClient();
  const meta = getIntegrationMeta(integrationId);
  const [confirmOpen, setConfirmOpen] = React.useState(false);

  const { data, loading, refetch } = useGenericDashboardQuery({
    client,
    variables: { integrationId: integrationId as IntegrationId },
    fetchPolicy: "cache-and-network",
    skip: mode === "create",
  });

  const [addMutation, { loading: addLoading }] =
    useIntegrationSettings_AddMutation({
      client,
      context: { noErrorNotification: true },
      refetchQueries: [namedOperations.Query.IntegrationsOverview],
    });

  const [removeMutation, { loading: removeLoading }] =
    useIntegrationsOverview_RemoveMutation({
      client,
      context: { noErrorNotification: true },
      refetchQueries: [namedOperations.Query.IntegrationsOverview],
      awaitRefetchQueries: true,
    });

  const orgIntegration = getDataOrNull(
    data?.organisationIntegration
  )?.organisationIntegration;

  const handleConnect = async () => {
    try {
      await addMutation({
        variables: {
          input: {
            integrationId: integrationId as IntegrationId,
          },
        },
      });
      navigate(INTEGRATIONS_BASE_PATH);
    } catch (e) {
      enqueueSnackbar(
        integrationErrorMessage(
          e,
          t("Something went wrong. Please try again.", { ns: "Global" })
        ),
        {
          variant: "error",
        }
      );
    }
  };

  const handleDisconnect = async () => {
    try {
      await removeMutation({
        variables: {
          input: { integrationId: integrationId as IntegrationId },
        },
      });
      navigate(INTEGRATIONS_BASE_PATH);
    } catch (e) {
      enqueueSnackbar(
        integrationErrorMessage(
          e,
          t("Something went wrong. Please try again.", { ns: "Global" })
        ),
        {
          variant: "error",
        }
      );
    }
  };

  const displayName = meta.sidebarLabel ?? integrationId;

  if (mode === "create") {
    const createBreadcrumbs: BreadcrumbItem[] = [
      {
        title: t("Organisation", { ns: "OrganisationPageTopbar" }),
        to: "/organisation",
      },
      { title: t("Integrations"), to: INTEGRATIONS_BASE_PATH },
      { title: displayName, to: integrationSettingsPath(integrationId) },
    ];

    return (
      <Page
        title={`${t("Connect")} ${displayName}`}
        breadcrumbs={createBreadcrumbs}
        breadcrumbsSize="medium"
      >
        <PageContainer>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 8,
              px: 2,
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 2,
                backgroundColor: meta.color,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 3,
              }}
            >
              <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700 }}>
                {meta.shortName}
              </Typography>
            </Box>
            <Typography variant="h6" gutterBottom>
              {displayName}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 3, maxWidth: 420, textAlign: "center" }}
            >
              {t(meta.description as any)}
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<PowerIcon />}
              disabled={addLoading}
              onClick={handleConnect}
            >
              {addLoading ? "..." : t("Connect")}
            </Button>
          </Box>
        </PageContainer>
      </Page>
    );
  }

  if (!orgIntegration && !loading) {
    return <Navigate to={INTEGRATIONS_BASE_PATH} replace />;
  }

  if (loading && !orgIntegration) return <LoadingSpinner />;

  const integrationName =
    meta.sidebarLabel ?? orgIntegration?.integration.name ?? integrationId;

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: t("Organisation", { ns: "OrganisationPageTopbar" }),
      to: "/organisation",
    },
    { title: t("Integrations"), to: INTEGRATIONS_BASE_PATH },
    { title: integrationName, to: integrationSettingsPath(integrationId) },
  ];

  return (
    <Page
      title={`${integrationName} — ${t("Settings")}`}
      breadcrumbs={breadcrumbs}
      breadcrumbsSize="medium"
    >
      <PageContainer>
        <Stack spacing={2} sx={{ width: "100%", maxWidth: 640 }}>
          <NoSettingsCard description={t(meta.description as any)}>
            <Stack direction="row" spacing={1}>
              {meta.category && (
                <Chip
                  label={t(meta.category as any)}
                  size="small"
                  variant="outlined"
                />
              )}
              <StatusChip
                label={t("Connected")}
                size="small"
                backgroundColor="#e8f5e9"
                textColor="#2e7d32"
              />
            </Stack>
          </NoSettingsCard>

          {!!orgIntegration?.health && (
            <Stack direction="row" sx={{ justifyContent: "flex-end" }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() =>
                  navigate(integrationDashboardPath(integrationId))
                }
              >
                {t("Dashboard")}
              </Button>
            </Stack>
          )}

          {(orgIntegration?.events?.length ?? 0) > 0 && (
            <ActivityFeed
              events={(orgIntegration?.events ?? []) as ActivityFeedEntry[]}
              onRefresh={refetch}
            />
          )}

          {/* Disconnect section */}
          <CardContainer title={t("Disconnect")}>
            <Stack
              direction="row"
              sx={{
                justifyContent: "space-between",
                alignItems: "center",
                px: 1,
                pt: 0.5,
                pb: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {t(
                  "No longer using this integration? You can disconnect it here."
                )}
              </Typography>
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={() => setConfirmOpen(true)}
                disabled={removeLoading}
              >
                {t("Disconnect")}
              </Button>
            </Stack>
          </CardContainer>
        </Stack>
      </PageContainer>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>
          {t("Disconnect {name}?", {
            name: integrationName,
            ns: "Integrations",
          })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t(
              "The connection will be removed. Data already transferred remains in MeisterSystems. To reconnect you may need your access credentials."
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={() => setConfirmOpen(false)}>
            {t("Cancel")}
          </Button>
          <Button
            color="error"
            onClick={() => {
              setConfirmOpen(false);
              handleDisconnect();
            }}
          >
            {t("Disconnect")}
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
};
